"use client";

import { useAuth } from "@/components/AuthContext";
import { Menu, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

const navLinks = [
  { label: "Features", href: "/landing#features" },
  { label: "AI Tutors", href: "/ai" },
  { label: "Practice", href: "/practice" },
  { label: "Tools", href: "/tools" },
  { label: "Pricing", href: "/landing#pricing" },
  { label: "FAQ", href: "/landing#faq" },
];

export function LandingNavbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isLoading } = useAuth();

  const getButtonText = () => {
    if (isLoading) return "...";
    return user ? "Go to App" : "Start free demo";
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative flex h-16 items-center justify-between lg:h-20">
          {/* Logo */}
          <Link
            href="/"
            className="absolute left-1/2 flex -translate-x-1/2 items-center gap-2 lg:static lg:translate-x-0"
          >
            <Image
              src="/ui/Chickylogovector.gif"
              alt="ChickyTutor"
              width={40}
              height={40}
              className="h-9 w-9 lg:h-12 lg:w-12"
            />
            <span className="whitespace-nowrap text-xl font-extrabold leading-none tracking-tighter text-gray-900 sm:text-2xl lg:text-3xl xl:text-4xl">
              Chickytutor.com
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden items-center gap-5 lg:flex xl:gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="text-gray-600 hover:text-gray-900 transition-colors font-bold"
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Sign up Button */}
          <div className="flex items-center gap-4 ml-auto md:ml-0">
            <Link
              href={user ? "/" : "#demo"}
              className="hidden items-center justify-center whitespace-nowrap rounded-full border-2 border-gray-900 px-5 py-2.5 font-bold text-gray-900 transition-colors hover:bg-gray-900 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A8431F] focus-visible:ring-offset-2 lg:inline-flex"
            >
              {getButtonText()}
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-gray-600 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A8431F] lg:hidden"
              aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={mobileMenuOpen}
              aria-controls="landing-mobile-navigation"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div id="landing-mobile-navigation" className="border-t border-gray-100 py-4 lg:hidden">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-gray-600 hover:text-gray-900 font-bold py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                href={user ? "/" : "#demo"}
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-full border-2 border-gray-900 text-gray-900 font-semibold hover:bg-gray-900 hover:text-white transition-all duration-200"
                onClick={() => setMobileMenuOpen(false)}
              >
                {getButtonText()}
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
